import React, { useState } from "react";
import { FilePicker } from "./InitiativeAttachments";

/**
 * The Complete action, with an optional place to attach evidence.
 *
 * Completing used to be a one-click row action that fired immediately. It now opens
 * this dialog because completion is the moment the paperwork exists — the invoice
 * has been paid, the photographs have been taken — and asking later means asking
 * when nobody is looking.
 *
 * Optional is the whole point: "Complete" is enabled with nothing picked and nothing
 * typed, and the wording says so. A required upload would turn a one-click action
 * into a blocker for anyone who did the work but has not scanned the receipt.
 *
 * The dates are here for the same reason. The API already accepts implemented_on
 * and verified_on on the status transition, and defaults implemented_on to today
 * when completing, so leaving both blank behaves exactly as the old button did.
 */
export default function CompleteInitiativeDialog({
  initiative,
  busy,
  error,
  onCancel,
  onConfirm,
}) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [implementedOn, setImplementedOn] = useState(
    initiative.implemented_on || ""
  );
  const [verifiedOn, setVerifiedOn] = useState(initiative.verified_on || "");

  const submit = () =>
    onConfirm({
      files,
      note: note.trim() || null,
      implemented_on: implementedOn || null,
      verified_on: verifiedOn || null,
    });

  return (
    <div className="ei-modal-overlay" onClick={busy ? undefined : onCancel}>
      <div className="ei-modal narrow" onClick={(e) => e.stopPropagation()}>
        <div className="ei-modal-header">
          <h3>Complete initiative</h3>
          <button
            className="ei-modal-close"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="ei-modal-body">
          <p style={{ margin: 0, fontSize: "0.9rem", color: "#475569" }}>
            <strong>{initiative.reference_code}</strong> — {initiative.title}
          </p>

          <div className="ei-form-grid" style={{ marginTop: "1rem" }}>
            <div className="ei-field">
              <label htmlFor="ei-complete-implemented">Implemented on</label>
              <input
                id="ei-complete-implemented"
                type="date"
                className="ei-input"
                value={implementedOn}
                onChange={(e) => setImplementedOn(e.target.value)}
                disabled={busy}
              />
              <p className="ei-hint">Blank = today</p>
            </div>
            <div className="ei-field">
              <label htmlFor="ei-complete-verified">Verified on</label>
              <input
                id="ei-complete-verified"
                type="date"
                className="ei-input"
                value={verifiedOn}
                onChange={(e) => setVerifiedOn(e.target.value)}
                disabled={busy}
              />
              <p className="ei-hint">Optional.</p>
            </div>
          </div>

          <div className="ei-field" style={{ marginTop: "0.5rem" }}>
            <label>Supporting documents — optional</label>
            <FilePicker
              files={files}
              onChange={setFiles}
              disabled={busy}
              inputId="ei-complete-files"
            />
          </div>

          <div className="ei-field" style={{ marginTop: "0.75rem" }}>
            <label htmlFor="ei-complete-note">Note — optional</label>
            <textarea
              id="ei-complete-note"
              className="ei-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was done, who signed it off."
              disabled={busy}
            />
          </div>

          {error && <p className="ei-file-error">{error}</p>}
        </div>

        <div className="ei-modal-footer">
          <button className="ei-secondary-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="ei-primary-btn" onClick={submit} disabled={busy}>
            {busy
              ? "Completing…"
              : files.length > 0
              ? `Complete and attach ${files.length} file(s)`
              : "Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}
